import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import { every, isEmpty, pickBy } from "lodash";
import { endOfMonth, parse } from "date-fns";
import { diff } from "deep-object-diff";

export const ACCOUNT_FIELDS = {
  // Account info
  type: "",
  category: "",
  name: "",
  description: "",
  accountNumber: "",
  routingNumber: "",
  ccOwner: null,
  ccType: "",
  ccLast4: "",
  ccExp: "",
  startingBalance: null,
  reconciledBalance: null,
  currentBalance: null,
  lastReconciled: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(ACCOUNT_FIELDS);

export const ACCOUNT_TYPES = {
  accountsPayable: {
    label: "Accounts Payable",
    icon: "account_payable",
  },
  accountsReceivable: {
    label: "Accounts Receivable",
    icon: "account_receivable",
  },
  bank: {
    label: "Bank",
    icon: "bank",
  },
  costOfGoodsSold: {
    label: "Cost of Goods Sold",
    icon: "window",
  },
  creditCard: {
    label: "Credit Card",
    icon: "credit_card",
  },
  equity: {
    label: "Equity",
    icon: "company",
  },
  fixedAsset: {
    label: "Fixed Asset",
    icon: "fixed_asset",
  },
  otherCurrentAsset: {
    label: "Other Current Asset",
    icon: "other_asset",
  },
  otherAsset: {
    label: "Other Asset",
    icon: "other_asset",
  },
  longTermLiability: {
    label: "Long Term Liability",
    icon: "liability",
  },
  otherCurrentLiability: {
    label: "Other Current Liability",
    icon: "liability",
  },
  income: {
    label: "Income",
    icon: "income",
  },
  otherIncome: {
    label: "Other Income",
    icon: "income",
  },
  expense: {
    label: "Expense",
    icon: "expense",
  },
  otherExpense: {
    label: "Other Expense",
    icon: "expense",
  },
  nonPosting: {
    label: "Non-Posting",
    icon: "non_posting",
  },
};
deepFreeze(ACCOUNT_TYPES);

export default class Account extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, ACCOUNT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Account.canBeViewedBy,
      create: Account.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("accounting"));
  }

  static canBeCreatedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("accounting"));
  }

  canBeEditedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("accounting"));
  }

  canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Account.getPath(this.id);
  }

  static getPath(id) {
    let path = `accounts`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(account) {
    return account.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Account(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      type: [
        (a) => Boolean(a) || "required",
        (t) => t in ACCOUNT_TYPES || "accountTypeInvalid",
      ],
      name: [
        (a) => this.data.type === "creditCard" || Boolean(a) || "required",
      ],
      ccType: [
        (a) => this.data.type !== "creditCard" || Boolean(a) || "required",
      ],
      ccLast4: [
        (a) =>
          this.data.type !== "creditCard" ||
          (Boolean(a) && a.length === 4) ||
          "required",
      ],
      ccExp: [
        (a) => {
          if (this.data.type !== "creditCard") {
            return true;
          }
          const now = new Date();
          return (
            (Boolean(a) &&
              /\d\d\/\d\d/.test(a) &&
              endOfMonth(parse(a, "MM/yy", now)) >= now) ||
            "ccExpInvalid"
          );
        },
      ],
      startingBalance: [(a) => a !== null || "required"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, ACCOUNT_FIELDS)));
  }

  isEqual(account) {
    return Boolean(
      this.id === account.id && isEmpty(diff(this.data, account.data))
    );
  }

  // Formatting

  get type() {
    return ACCOUNT_TYPES[this.data.type];
  }

  get startingBalance() {
    return formatCurrency(this.data.startingBalance);
  }

  get reconciledBalance() {
    return formatCurrency(this.data.reconciledBalance);
  }

  get currentBalance() {
    return formatCurrency(this.data.currentBalance);
  }

  get transactions() {
    return pickBy(
      store.state.main.transactions,
      (tx) => tx.data.to === this.id || tx.data.from === this.id
    );
  }

  get ccOwner() {
    return this.data.ccOwner ? store.state.main.users[this.data.ccOwner] : null;
  }

  get ccLast4() {
    return "**** **** **** " + this.data.ccLast4;
  }

  get ccExpDate() {
    return endOfMonth(parse(this.data.ccExp, "MM/yy", new Date()));
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  toString() {
    return this.data.name;
  }
}
